import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as fromGraphQl from './graphql';
import { Issue } from '../../entities/issue/issue';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { CommandInput, CommandTemplate } from '../../entities/commandTemplate/commandTemplate';
import { CommandService, Reloadable } from '../../services/command/command.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivityStreamComponent } from '../activity-stream/activity-stream.component';
import { WidgetCoordinatorService } from '../../services/issue/widget-coordinator.service';
import { ReactiveContainer } from '../../containers/reactive-container';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { SIDEBAR_CONTEXT } from '../../services/sidebar/sidebar-context';

@Component({
  selector: 'app-issue-view',
  templateUrl: './issue-view.component.html',
  styleUrls: ['./issue-view.component.scss'],
  providers: [WidgetCoordinatorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.in-sidebar]': 'isInSidebar'
  }
})
export class IssueViewComponent implements OnInit, OnDestroy, Reloadable {

  // View children
  readonly activityStream = viewChild(ActivityStreamComponent);

  // Input parameters
  showBreadcrumb = input<boolean>(true);

  // Injected dependencies
  private readonly route = inject(ActivatedRoute);
  readonly commandService = inject(CommandService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);

  // Sidebar context - true if this component is rendered inside a sidebar
  readonly isInSidebar = inject(SIDEBAR_CONTEXT, { optional: true }) ?? false;

  // Container for GraphQL query (composition over inheritance)
  private readonly container = new ReactiveContainer<fromGraphQl.IssueQueryResponse>(
    fromGraphQl.ISSUE_QUERY,
    { stopPollingOnSuccess: true }
  );

  // Route param
  private id = '';

  // Refetch coordinator unregister function
  private unregisterRefetch?: () => void;

  // Signals for reactive state - issue is now derived from container.data
  readonly issue = computed(() => {
    const data = this.container.data();
    return data?.getIssue ? new Issue(data.getIssue) : null;
  });

  readonly loading = this.container.loading;
  readonly error = this.container.error;

  readonly menuVisible = signal(false);
  readonly isEditMode = signal(false);

  // Computed values - automatically update when issue changes
  readonly descriptionHtml = computed(() => {
    const issue = this.issue();
    if (!issue?.description) return '';
    try {
      return generateHTML(issue.description, [StarterKit]);
    } catch (error) {
      console.error(`Failed to render description for issue ${issue.id}`, error);
      return '';
    }
  });

  readonly menuItems = computed(() => {
    const issue = this.issue();
    if (!issue?.commands?.length) return [];

    return issue.commands.map((command: CommandTemplate) => ({
      label: command.settings?.label,
      disabled: command.settings?.disabled,
      command: () => {
        this.commandService.execute(issue, command, this);
      }
    }));
  });

  readonly updateCommandInput = computed(() => {
    const issue = this.issue();
    if (!issue) return undefined;

    const commandInput = new CommandInput();
    commandInput.name = 'issueUpdateCommand';
    commandInput.addParameter('commandProviderId', issue.id);
    return commandInput;
  });

  readonly createTaskCommandInput = computed(() => {
    const issue = this.issue();
    if (!issue) return undefined;

    const commandInput = new CommandInput();
    commandInput.name = 'issueAddTaskCommand';
    commandInput.addParameter('commandProviderId', issue.id);
    return commandInput;
  });

  // Signal-based form group - reactive to edit mode and issue changes
  readonly issueFormGroup = computed(() => {
    if (!this.isEditMode()) return null;
    const issue = this.issue();
    return new FormGroup({
      title: new FormControl(issue?.title, [Validators.required]),
      description: new FormControl(issue?.description, [Validators.required])
    });
  });

  // Edit mode callbacks
  private onSave: ((data: any) => void) | undefined;
  private onCancel: (() => void) | undefined;

  private static readonly COMMAND_INPUTS = [
    { name: 'issueCloseCommand' },
    { name: 'issueReopenCommand' },
    { name: 'issueDeleteCommand' }
  ];

  reload(): void {
    this.container.refetch(this.getVariables());
    this.activityStream()?.reload(true);
  }

  ngOnInit(): void {
    // Support both /issues/issue/:id and /issues?show=:id
    this.id = this.route.snapshot.params['id'] || this.route.snapshot.queryParams['show'];
    this.container.execute(this.getVariables());

    // Register as detail view with entity ID
    this.unregisterRefetch = this.refetchCoordinator.register(this, Issue.ENTITY_TYPE, this.id);
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  private getVariables(): { id: string; commandInputs: { name: string }[] } {
    return {
      id: this.id,
      commandInputs: IssueViewComponent.COMMAND_INPUTS
    };
  }

  enterEditMode(): Promise<{ action: 'save' | 'cancel'; data?: any }> {
    return new Promise((resolve) => {
      this.isEditMode.set(true);

      this.onSave = (data: any) => {
        this.isEditMode.set(false);
        resolve({ action: 'save', data });
      };

      this.onCancel = () => {
        this.isEditMode.set(false);
        resolve({ action: 'cancel' });
      };
    });
  }

  saveChanges(): void {
    this.onSave?.(this.issueFormGroup()?.value);
  }

  cancelChanges(): void {
    this.onCancel?.();
  }

  onMenuShow(): void {
    this.menuVisible.set(true);
  }

  onMenuHide(): void {
    this.menuVisible.set(false);
  }

  isDescriptionEmpty(description: string | null | undefined): boolean {
    if (!description) return true;
    const trimmed = description.trim();
    return trimmed === '' || trimmed === '<p></p>' || trimmed === '<p><br></p>';
  }
}
